// WP-08 (Fase 3 del plan de Reportería) — Tab "Moderación": KPIs agregados + detalle de casos
// reportados, con datos de contacto para escalamiento (RN-M07). A diferencia del resto de tabs de
// Reportería, esta pestaña NO vive en AnalyticsService: reutiliza el repositorio de moderación de
// RecognitionService (colección `reports`), que ya trae nombre/correo/OU/motivo resueltos — sin
// ids crudos que resolver en el front. SIN puntos.
//
// G3/G5-web — pinta `mensajePreview`/`idioma`/`numeroReportes` (los dos últimos, nuevos de la Tarea
// G1) y agrega el export a Excel multi-hoja ("07 - Moderacion.xlsx") que faltaba frente al resto de
// tabs de Reportería. `POST v1/moderation/report-export-audit` es SOLO auditoría (bitácora propia de
// RecognitionService, fire-and-forget): el Excel lo arma este componente client-side con los datos
// que ya trajo `getReport`.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModerationReport, useModerationSettings, moderationApi } from '@/api/recognition';
import type { ModerationStatus, RecognitionReportParams } from '@/api/recognition';
import { exportMultiSheetExcel } from '@/utils/exportExcelMultiSheet';
import type { ExcelCellValue, ExcelCellStyleHint, ExcelSheetSpec } from '@/utils/exportExcelMultiSheet';
import { ReportStatus, KpiRow } from '../reportsShared';
import { AvisoFiltroNoAplica } from '../AvisoFiltroNoAplica';
import { Paginacion, usePaginacion } from '../Paginacion';

const SIN_DATO = '—';

function statusKey(estado: ModerationStatus): 'statusPending' | 'statusHidden' | 'statusRestored' | 'statusPublished' {
  if (estado === 'Pendiente') return 'statusPending';
  if (estado === 'Resuelto') return 'statusHidden';
  if (estado === 'Desestimado') return 'statusRestored';
  return 'statusPublished';
}

/**
 * Texto LITERAL en español del Estado para el Excel — mismo criterio que el resto de los reportes
 * de esta pestaña (`CampaignsGlobalExportButton.tsx`/`CampaignRowActions.tsx`): el contenido del
 * archivo no cambia con el idioma de quien lo exporta, a diferencia de la pantalla interactiva
 * (que sí traduce vía `t()`, ver `statusKey` arriba).
 */
function estadoLiteral(estado: ModerationStatus): string {
  if (estado === 'Pendiente') return 'Pendiente';
  if (estado === 'Resuelto') return 'Oculto';
  if (estado === 'Desestimado') return 'Restaurado';
  return 'Publicado';
}

// Paleta PROPIA de "07 - Moderacion.xlsx" (medida celda a celda, 2026-09-03) — distinta de
// `excelColors` (medido de otro archivo de referencia para el resto de los reportes de Reportería).
// Por eso esta hoja usa `isDefaultThemeSkipped` + `cellStyles` en vez del tema compartido.
const MOD_NAVY = 'FF003B71';
const MOD_NAVY_ACCENT = 'FF0072CE'; // celda vacía junto al header de KPI (D7 del archivo original)
const MOD_WHITE = 'FFFFFFFF';
const MOD_CREAM = 'FFFFF4BF'; // "Umbral configurable"/"Generado" (valor) y la fila "Pendientes"
const MOD_LIGHT_BLUE = 'FFEAF4FF'; // subtítulo, "Valor" de 3 de los 4 KPIs, Definiciones."Dato"
const MOD_MUSTARD = 'FFFFD100'; // Estado = Pendiente
const MOD_BODY_TEXT = 'FF1F2933';

/**
 * Los 3 colores de "GUÍA DE ESTADOS", reutilizados tal cual en la columna «Estado» de "Reportes
 * Detalle" — mismo criterio en los dos sitios, para que la leyenda y la tabla nunca discrepen.
 */
function coloresDeEstado(estado: ModerationStatus): { fillArgb: string; fontColorArgb: string } {
  if (estado === 'Pendiente') return { fillArgb: MOD_MUSTARD, fontColorArgb: MOD_NAVY };
  if (estado === 'Resuelto') return { fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE };
  return { fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY }; // Restaurado (Desestimado) y respaldo
}

/**
 * 2026-09-04 — este tab SÍ consume la barra de filtros. `v1/moderation/report` acepta OU, rango de
 * fechas y persona (los tres se aplican sobre lo que la tabla enseña: la columna OU, la columna
 * Fecha y las dos partes del caso). El único que no aplica es Fundamental: el reporte se arma sobre
 * los casos reportados, no sobre los Fundamentales del reconocimiento — y en vez de aceptarlo y
 * descartarlo en silencio, la pantalla lo declara con `AvisoFiltroNoAplica`.
 *
 * Antes el tab no recibía `params` y la barra se ocultaba entera, así que no había forma de acotar
 * la bandeja de casos ni por unidad ni por fecha.
 */
/** `canExportData` default `true`: ver nota en `DailyTab.tsx`. */
export function ModerationReportTab({
  params, filtrosAplicados = null, canExportData = true,
}: Readonly<{
  params: RecognitionReportParams;
  /** Filtros activos, ya con nombres resueltos, para declararlos en el Excel. Null ⇒ ninguno. */
  filtrosAplicados?: string | null;
  canExportData?: boolean;
}>) {
  const { t } = useTranslation('recognition');
  const q = useModerationReport(params);
  const data = q.data;
  // Umbral de auto-ocultamiento REAL (Moderación > Config), para la fila "Umbral configurable" del
  // export — nunca el "3" fijo que muestra el maquetado de referencia del cliente (por omisión es
  // 1, ver doc-comment de `ModerationSettingsDto.hideThreshold`): subirlo es decisión del admin.
  const settingsQuery = useModerationSettings();

  // Paginación estándar: 10 por omisión, escalonado hasta 200.
  const pag = usePaginacion(data?.casos ?? []);
  /**
   * SIN umbral de anonimato (decisión de negocio 2026-09-04): aquí cero casos significa una sola
   * cosa —no hay reportes—, así que el estado vacío es incondicional. El umbral protege agregados
   * de los que se puede inferir a una persona; este tab es nominal por diseño (nombre, correo y OU
   * de cada parte, para escalar el caso) y sirve exactamente las mismas filas que la bandeja de
   * moderación, que nunca las podó. Ver `ModerationReportDto` en SharedContracts.
   */
  const isEmpty = !!data && data.casos.length === 0;

  /*
   * Fix final (revisión de rama) — la migración xlsx→exceljs volvió async a `exportMultiSheetExcel`
   * y `handleExportModeration` quedó sin try/catch: un fallo (401/500) rechazaba la promesa SIN
   * manejar — cero mensaje, cero log. Mismo hallazgo ya corregido en `handleExportShine`
   * (`ShineTab.tsx`, fix round 1); el mensaje se REUSA de `reports.shine.exporting`/`exportError`
   * (ya genérico) en vez de duplicarlo.
   */
  const [exportandoModeracion, setExportandoModeracion] = useState(false);
  const [exportModeracionError, setExportModeracionError] = useState(false);

  /**
   * Export "07 - Moderacion.xlsx" — calcado CELDA A CELDA del archivo de referencia del cliente
   * (título/subtítulo en banner, fila "Umbral configurable"/"Generado", KPIs con una narrativa
   * ancha al lado, "GUÍA DE ESTADOS", y en "Reportes Detalle" las dos anotaciones sobre Comentario/
   * Motivo) — 3 hojas armadas a mano (no con `buildResumenEjecutivoSheet`/`buildDefinicionesSheet`,
   * que no tienen esta forma) porque el cliente pidió expresamente que el resultado fuera idéntico
   * a esa plantilla, no al patrón de 3 columnas que sí siguen el resto de los reportes.
   *
   * Dos diferencias A PROPÓSITO frente al archivo de referencia (decisión de negocio 2026-09-03):
   *   1. Sin columna «País» en "Reportes Detalle" — pedido explícito del cliente. La tabla en
   *      pantalla no se tocó (`caso.country` sigue ahí), el pedido es solo del reporte exportado.
   *   2. Sin el pie "Datos de ejemplo del entorno demo... no representan producción": esa leyenda
   *      describe el archivo de MUESTRA del cliente, no un reporte con datos reales — mantenerla
   *      aquí sería falso.
   * El título SÍ se deja literal ("Sigma Shine Together"), y la narrativa de los KPIs SÍ se genera
   * dinámica con los números reales — ambos confirmados con el cliente.
   *
   * Texto LITERAL en español (no `t()`): mismo criterio que el resto de las hojas de esta pestaña
   * (`CampaignsGlobalExportButton.tsx`) — el Excel no cambia con el idioma de quien lo exporta.
   */
  async function handleExportModeration() {
    if (!data) return;
    setExportandoModeracion(true);
    setExportModeracionError(false);
    try {
      const hideThreshold = settingsQuery.data?.hideThreshold ?? 1;
      const generado = new Date().toLocaleString('es-MX');

      // Los 4 KPIs (`data.total`/`pendientes`/`ocultos`/`restaurados`) YA vienen sumados por el
      // backend (suma de `numeroReportes`, no conteo de casos — ver doc-comment de
      // `GetModerationReportQueryHandler.Handle`); la narrativa solo los redacta en prosa.
      const totalCasos = data.casos.length;
      const narrativa =
        `La base contiene ${totalCasos} reconocimiento${totalCasos === 1 ? '' : 's'} reportado${totalCasos === 1 ? '' : 's'} `
        + `y ${data.total} reporte${data.total === 1 ? '' : 's'} acumulado${data.total === 1 ? '' : 's'}. `
        + `El principal foco de atención son los pendientes (${data.pendientes} reportes), seguidos de `
        + `casos ocultos (${data.ocultos}) y restaurados (${data.restaurados}). Usa el detalle para revisar `
        + 'contacto, motivo y comentario original antes de escalar.';

      // ── Hoja 1: Resumen Ejecutivo ──────────────────────────────────────────────────────────
      /*
       * Los filtros de la tira se DECLARAN en el subtítulo, que ya es la línea de contexto de la hoja
       * (merge A2:F2). Sin esto, un libro exportado con «OU: México» y una semana puesta se leería
       * como el universo completo de reportes — el mismo defecto que se corrigió en los otros cuatro
       * Excel de Reportería el 2026-09-03 («Filtros aplicados» solo llevaba la OU).
       *
       * Va aquí y no en una fila nueva a propósito: esta hoja está calcada celda a celda del archivo
       * de referencia del cliente y sus estilos/merges se direccionan por índice de fila, así que
       * insertar una fila desplazaría los 30 `cellStyles` de abajo.
       */
      const subtituloBase =
        'Detalle de reconocimientos reportados. Incluye datos de contacto para escalamiento.';
      const subtitulo = filtrosAplicados
        ? `${subtituloBase} Filtros aplicados: ${filtrosAplicados}.`
        : subtituloBase;

      const resumenRows: ExcelCellValue[][] = [
        ['Reportería de Moderación — Sigma Shine Together', null, null, null, null, null],
        [subtitulo, null, null, null, null, null],
        [],
        ['Umbral configurable', `${hideThreshold} reportes para ocultamiento automático (editable en Moderación > Config)`, null, null, null, null],
        ['Generado', generado, null, null, null, null],
        [],
        ['KPI', 'Valor', 'Detalle', null, null, null],
        ['Reportes totales', data.total, `${data.total} reportes acumulados en ${totalCasos} reconocimientos reportados`, narrativa, null, null],
        ['Pendientes', data.pendientes, `${data.pendientes} reportes requieren revisión`, null, null, null],
        ['Ocultos', data.ocultos, `${data.ocultos} reportes ya fueron ocultados`, null, null, null],
        ['Restaurados', data.restaurados, `${data.restaurados} reportes fueron restaurados`, null, null, null],
        [],
        ['GUÍA DE ESTADOS', null, null, null, null, null],
        ['Pendiente', 'Requiere revisión del moderador.', null, null, 'Color', 'Amarillo'],
        ['Oculto', 'El reconocimiento quedó oculto por decisión de moderación.', null, null, 'Color', 'Azul'],
        ['Restaurado', 'El moderador mantuvo visible el reconocimiento.', null, null, 'Color', 'Azul claro'],
      ];
      const resumenCellStyles: ExcelCellStyleHint[] = [
        { row: 0, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true, fontSize: 18 },
        { row: 1, column: 0, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isItalic: true, fontSize: 11 },
        { row: 3, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 3, column: 1, fillArgb: MOD_CREAM, fontColorArgb: MOD_NAVY },
        { row: 4, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 4, column: 1, fillArgb: MOD_CREAM, fontColorArgb: MOD_NAVY },
        { row: 6, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 6, column: 1, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 6, column: 2, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 6, column: 3, fillArgb: MOD_NAVY_ACCENT, fontColorArgb: MOD_WHITE, isBold: true },
        // Reportes totales — solo la columna "Valor" (B) se resalta.
        { row: 7, column: 0, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        { row: 7, column: 1, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isBold: true, fontSize: 14 },
        { row: 7, column: 2, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        // Pendientes — la fila ENTERA en crema, para destacarla como la de mayor atención.
        { row: 8, column: 0, fillArgb: MOD_CREAM, fontColorArgb: MOD_BODY_TEXT },
        { row: 8, column: 1, fillArgb: MOD_CREAM, fontColorArgb: MOD_NAVY, isBold: true, fontSize: 14 },
        { row: 8, column: 2, fillArgb: MOD_CREAM, fontColorArgb: MOD_BODY_TEXT },
        // Ocultos
        { row: 9, column: 0, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        { row: 9, column: 1, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isBold: true, fontSize: 14 },
        { row: 9, column: 2, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        // Restaurados
        { row: 10, column: 0, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        { row: 10, column: 1, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isBold: true, fontSize: 14 },
        { row: 10, column: 2, fillArgb: MOD_WHITE, fontColorArgb: MOD_BODY_TEXT },
        // GUÍA DE ESTADOS — mismos 3 colores que la columna «Estado» de "Reportes Detalle".
        { row: 12, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 13, column: 0, ...coloresDeEstado('Pendiente'), isBold: true },
        { row: 14, column: 0, ...coloresDeEstado('Resuelto'), isBold: true },
        { row: 15, column: 0, ...coloresDeEstado('Desestimado'), isBold: true },
      ];
      const sheetResumen: ExcelSheetSpec = {
        name: 'Resumen Ejecutivo',
        rows: resumenRows,
        styleHints: {
          isDefaultThemeSkipped: true,
          merges: ['A1:F1', 'A2:F2', 'B4:F4', 'B5:F5', 'D7:F7', 'D8:F11', 'A13:F13', 'B14:D14', 'B15:D15', 'B16:D16'],
          columnWidths: [30.44, 17.11, 41.89, 17.11, 17.11, 17.11],
          cellStyles: resumenCellStyles,
        },
      };

      // ── Hoja 2: Definiciones — calcada del cliente, no del helper de 3 columnas genérico ────
      const ultimaFilaDetalle = 5 + totalCasos; // fila 5 = encabezado de "Reportes Detalle"
      const definicionesRows: ExcelCellValue[][] = [
        ['¿Qué significa cada dato?', null, null],
        ['Explicación en lenguaje simple, para cualquier persona que descargue este archivo — sin necesidad de saber de datos.', null, null],
        [],
        [],
        ['Dato', 'Qué es', 'Cómo se calcula'],
        ['Reportes totales', 'Total de reportes recibidos contra reconocimientos marcados como inapropiados.',
          `Suma de # Reportes en Reportes Detalle!G6:G${ultimaFilaDetalle}.`],
        ['Pendientes', 'Reportes acumulados en reconocimientos que todavía no han sido revisados por un moderador.',
          `Suma de # Reportes cuando Estado = Pendiente en Reportes Detalle!H6:H${ultimaFilaDetalle}.`],
        ['Ocultos', 'Reportes acumulados en reconocimientos donde el moderador decidió ocultar el contenido.',
          `Suma de # Reportes cuando Estado = Oculto en Reportes Detalle!H6:H${ultimaFilaDetalle}.`],
        ['Restaurados', 'Reportes acumulados en reconocimientos donde el moderador decidió mantener visible el contenido.',
          `Suma de # Reportes cuando Estado = Restaurado en Reportes Detalle!H6:H${ultimaFilaDetalle}.`],
        ['Comentario reportado', 'El texto real del reconocimiento que se marcó como inapropiado.',
          'Se copia el contenido exacto del reconocimiento original para que un moderador entienda el contexto sin volver a la plataforma.'],
        ['Motivo del reporte',
          'La categoría que explica por qué se reportó: falta de respeto, sarcasmo, burla, tono directo o contexto aclarado.',
          'Se asigna al resolver o clasificar el reporte; ayuda a explicar por qué un caso se oculta, queda pendiente o se restaura.'],
        ['Reportante',
          'Quien reporta el reconocimiento porque considera que infringe una política o puede herir directa o indirectamente a alguien.',
          'Persona que da clic en el botón «reportar».'],
        ['Reportado', 'Autor del reconocimiento reportado.', 'Persona a quien se le reporta el reconocimiento por su contribución.'],
      ];
      const definicionesCellStyles: ExcelCellStyleHint[] = [
        { row: 0, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true, fontSize: 18 },
        { row: 1, column: 0, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isItalic: true, fontSize: 11 },
        { row: 4, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 4, column: 1, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        { row: 4, column: 2, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true },
        // Columna «Dato» — TODAS las filas de datos (5..12), no solo alguna: mismo estilo en las 8.
        // `hasWrapText` para que "Comentario reportado"/"Motivo del reporte" (más largos que el resto)
        // se ajusten dentro del ancho de columna medido (26.66) en vez de desbordarse.
        ...definicionesRows.slice(5).map((_, i): ExcelCellStyleHint => (
          { row: 5 + i, column: 0, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isBold: true, hasWrapText: true }
        )),
        // Columnas «Qué es»/«Cómo se calcula» — mismo ajuste, son párrafos largos.
        ...definicionesRows.slice(5).flatMap((_, i): ExcelCellStyleHint[] => [
          { row: 5 + i, column: 1, hasWrapText: true },
          { row: 5 + i, column: 2, hasWrapText: true },
        ]),
      ];
      const sheetDefiniciones: ExcelSheetSpec = {
        name: 'Definiciones',
        rows: definicionesRows,
        styleHints: {
          isDefaultThemeSkipped: true,
          merges: ['A1:C1', 'A2:C2'],
          columnWidths: [26.66, 41.89, 43.89],
          cellStyles: definicionesCellStyles,
        },
      };

      // ── Hoja 3: Reportes Detalle — sin País (requisito del cliente, ver doc-comment de arriba) ─
      const detalleRows: ExcelCellValue[][] = [
        ['Reportes Detalle', null, null, null, null, null, null, null, null, null],
        ['Listado de reconocimientos reportados con datos de contacto', null, null, null, null, null, null, null, null, null],
        [],
        [null, null, null, null, null, null, null, null, 'Reconocimiento / nominación ', 'Justificación'],
        [
          'Reportado (autor)', 'Email reportado', 'OU', 'Idioma', 'Reportante', 'Email reportante',
          '# Reportes', 'Estado', 'Comentario reportado', 'Motivo del reporte',
        ],
        ...data.casos.map((caso): ExcelCellValue[] => [
          caso.reportado.nombre, caso.reportado.correo ?? '',
          caso.ouName ?? '', caso.idioma ?? SIN_DATO,
          caso.reportante.nombre, caso.reportante.correo ?? '',
          caso.numeroReportes ?? SIN_DATO,
          estadoLiteral(caso.estado),
          caso.mensajePreview ?? SIN_DATO, caso.motivo,
        ]),
      ];
      const COL_ESTADO = 7; // Reportado(0) Email(1) OU(2) Idioma(3) Reportante(4) Email(5) #Reportes(6) Estado(7) Comentario(8) Motivo(9)
      const detalleCellStyles: ExcelCellStyleHint[] = [
        { row: 0, column: 0, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true, fontSize: 18 },
        { row: 1, column: 0, fillArgb: MOD_LIGHT_BLUE, fontColorArgb: MOD_NAVY, isItalic: true, fontSize: 11 },
        // Header (fila 4, 0-indexed = fila 5 de Excel): las 10 columnas.
        ...Array.from({ length: 10 }, (_, col): ExcelCellStyleHint => (
          { row: 4, column: col, fillArgb: MOD_NAVY, fontColorArgb: MOD_WHITE, isBold: true }
        )),
        // Columna «Estado» por valor — MISMOS 3 colores que "GUÍA DE ESTADOS" (coloresDeEstado).
        ...data.casos.map((caso, i): ExcelCellStyleHint => (
          { row: 5 + i, column: COL_ESTADO, ...coloresDeEstado(caso.estado), isBold: true }
        )),
        // «Comentario reportado»/«Motivo del reporte» — texto libre largo, se ajusta a la celda.
        ...data.casos.flatMap((_, i): ExcelCellStyleHint[] => [
          { row: 5 + i, column: 8, hasWrapText: true },
          { row: 5 + i, column: 9, hasWrapText: true },
        ]),
      ];
      const sheetDetalle: ExcelSheetSpec = {
        name: 'Reportes Detalle',
        rows: detalleRows,
        styleHints: {
          isDefaultThemeSkipped: true,
          merges: ['A1:J1', 'A2:J2'],
          columnWidths: [22.89, 30.44, 10.44, 10.44, 22.89, 30.44, 14.33, 17.55, 38.11, 30.44],
          cellStyles: detalleCellStyles,
        },
      };

      await exportMultiSheetExcel({
        fileName: 'reporteria-moderacion',
        sheets: [sheetResumen, sheetDefiniciones, sheetDetalle],
      });

      // Fire-and-forget (mismo patrón que `auditDemograficoExport`/`auditLeadersListExport`): NUNCA
      // bloquea la descarga, que ya se completó arriba. Si falla, se loguea (guard DEV) — la bitácora
      // perdió UN registro, no la descarga del usuario.
      moderationApi.auditReportExport(data.casos.length).catch((error: unknown) => {
        if (import.meta.env.DEV) console.error('No se pudo registrar la auditoria del export de Moderacion:', error);
      });
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('No se pudo generar el export de Moderacion:', error);
      setExportModeracionError(true);
    } finally {
      setExportandoModeracion(false);
    }
  }

  return (
    <div className="au-card">
      <div className="au-card-header">
        <div>
          <div className="au-card-title">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.moderation.title')}
          </div>
          <div className="au-card-sub">{t('reports.moderation.subtitle')}</div>
        </div>
      </div>

      {/*
        El aviso va ARRIBA del estado de carga y no dentro: si el Fundamental puesto deja la tabla en
        vacío, quien mira necesita leer justo entonces que ese filtro no es el motivo. Dentro de
        `ReportStatus` no se pintaría en los estados de carga, error ni vacío.
      */}
      <AvisoFiltroNoAplica
        filtros={params.fundamentalId ? ['fundamental'] : []}
        motivo="casosReportados"
      />

      <ReportStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        isEmpty={isEmpty}
        onRetry={() => q.refetch()}
      >
        {data && (
          <>
            <KpiRow
              items={[
                { label: t('reports.moderation.total'), value: data.total, color: '#342F82' },
                { label: t('reports.moderation.pending'), value: data.pendientes, color: '#F08521' },
                { label: t('reports.moderation.hidden'), value: data.ocultos, color: '#E11F26' },
                { label: t('reports.moderation.restored'), value: data.restaurados, color: '#20B15A' },
              ]}
            />

            {canExportData && (
              <div className="rr-actions rr-actions--top-gap">
                <button
                  type="button"
                  className="rr-btn"
                  onClick={handleExportModeration}
                  disabled={data.casos.length === 0 || exportandoModeracion}
                >
                  <i
                    className={exportandoModeracion ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
                    aria-hidden="true"
                  />{' '}
                  {exportandoModeracion ? t('reports.shine.exporting') : t('reports.moderation.export')}
                </button>
              </div>
            )}

            {/* Mismo hallazgo que `handleExportShine` (fix round 1, `ShineTab.tsx`): sin esto un fallo
                del export quedaba en silencio, sin mensaje ni log. */}
            {exportModeracionError && (
              <p className="rr-state rr-state--error" role="alert">
                <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {t('reports.shine.exportError')}
              </p>
            )}

            <div className="rr-table-wrap rr-table-wrap--top-gap">
              <table className="rr-table">
                <thead>
                  <tr>
                    <th>{t('reports.moderation.colReported')}</th>
                    <th>{t('reports.moderation.colReportedEmail')}</th>
                    <th>{t('reports.moderation.colOu')}</th>
                    <th>{t('reports.moderation.colCountry')}</th>
                    <th>{t('reports.moderation.colReporter')}</th>
                    <th>{t('reports.moderation.colReporterEmail')}</th>
                    <th>{t('reports.moderation.colReason')}</th>
                    <th>{t('reports.moderation.colComment')}</th>
                    <th>{t('reports.moderation.colLanguage')}</th>
                    <th>{t('reports.moderation.colReportCount')}</th>
                    <th>{t('reports.moderation.colStatus')}</th>
                    <th>{t('reports.moderation.colDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pag.visibles.map((caso) => (
                    <tr key={caso.id}>
                      <td>{caso.reportado.nombre}</td>
                      <td>{caso.reportado.correo ?? ''}</td>
                      <td>{caso.ouName ?? ''}</td>
                      <td>{caso.country ?? ''}</td>
                      <td>{caso.reportante.nombre}</td>
                      <td>{caso.reportante.correo ?? ''}</td>
                      <td>{caso.motivo}</td>
                      <td>{caso.mensajePreview ?? SIN_DATO}</td>
                      <td>{caso.idioma ?? SIN_DATO}</td>
                      <td>{caso.numeroReportes ?? SIN_DATO}</td>
                      <td>{t(`reports.moderation.${statusKey(caso.estado)}`)}</td>
                      <td>{new Date(caso.fecha).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*
              `siempreVisible` — la bandeja de casos es corta por naturaleza (en una organización
              sana son unas pocas filas), así que con la regla por omisión el pie no se pintaba nunca
              y la pantalla se quedaba sin el rango «1–3 de 3» ni el selector de tamaño. Aquí el pie
              informa aunque no haya que navegar: dice cuántos casos hay en total tras el filtro.
            */}
            <Paginacion
              pagina={pag.pagina} totalPaginas={pag.totalPaginas} total={pag.total}
              tamano={pag.tamano} onPagina={pag.setPagina} onTamano={pag.cambiarTamano}
              siempreVisible
            />
          </>
        )}
      </ReportStatus>
    </div>
  );
}
